import { customAlphabet } from 'nanoid';
import { readCookie, writeCookie, deleteCookie } from './cookie-util';
import { LOCAL_SESSIONS_KEY, SESSION_COOKIE_KEY } from '../../consts/common-consts';

// Same alphabet/length as the web-plugin, so ids stay interchangeable between
// the two clients and remain safe inside a URL.
const nanoid = customAlphabet('1234567890abcdefghikkl', 18);

export function newSessionId() {
  return nanoid(15);
}

/**
 * Resolve the session id for this visit, in priority order:
 *
 *   1. `?session_id=` - an explicit, shareable link to one conversation
 *   2. the session cookie - the returning visitor's own conversation
 *   3. a freshly minted id, which is then persisted to the cookie
 *
 * This is the behaviour the web-plugin lacks: it minted a new id on every
 * mount, so a reload always started an empty conversation. Only case 3 writes,
 * so an explicit `session_id` never overwrites the cookie of whoever opened
 * the link.
 */
let resolvedForThisPageLoad = null;

export function resolveSessionId(sessionIdParam) {
  if (sessionIdParam) {
    return { sessionId: sessionIdParam, created: false, source: 'query' };
  }

  // Memoized for the lifetime of the page load. Minting writes the cookie, so
  // a second call - a StrictMode remount, or any component resolving again -
  // would otherwise read that cookie back and report `created: false`, losing
  // the fact that this session is brand new (and with it the opening
  // greeting).
  if (resolvedForThisPageLoad) {
    return resolvedForThisPageLoad;
  }

  const cookieSessionId = readCookie(SESSION_COOKIE_KEY);
  if (cookieSessionId) {
    resolvedForThisPageLoad = { sessionId: cookieSessionId, created: false, source: 'cookie' };
    return resolvedForThisPageLoad;
  }

  const sessionId = newSessionId();
  writeCookie(SESSION_COOKIE_KEY, sessionId);
  resolvedForThisPageLoad = { sessionId, created: true, source: 'new' };
  return resolvedForThisPageLoad;
}

/**
 * Drop the memoized resolution.
 *
 * Exported so tests can simulate a fresh page load; in the app itself the memo
 * lives exactly as long as the document does.
 */
export function clearResolvedSession() {
  resolvedForThisPageLoad = null;
}

/**
 * Start a brand new conversation and make it the remembered one.
 * Used by the "New chat" and "Reset session" actions.
 */
export function startNewSession() {
  const sessionId = newSessionId();
  writeCookie(SESSION_COOKIE_KEY, sessionId);
  // The new session, not the one resolved at page load, is now current.
  resolvedForThisPageLoad = { sessionId, created: true, source: 'new' };
  return sessionId;
}

/** Adopt an existing conversation (from the history rail) as the current one. */
export function rememberSessionId(sessionId) {
  if (sessionId) {
    writeCookie(SESSION_COOKIE_KEY, sessionId);
    resolvedForThisPageLoad = { sessionId, created: false, source: 'cookie' };
  }
}

export function forgetSessionId() {
  deleteCookie(SESSION_COOKIE_KEY);
  clearResolvedSession();
}

/* ------------------------------------------------------------------ *
 * Local session index
 *
 * `/apps/{appId}/sessions` sits behind the Cognito authorizer, so a guest
 * holding only an app access key cannot list conversations from it. To keep
 * the history rail useful anyway, every session this browser takes part in is
 * indexed locally and merged with whatever the API returns.
 * ------------------------------------------------------------------ */

const MAX_LOCAL_SESSIONS = 50;

function storageKey(appId) {
  return `${LOCAL_SESSIONS_KEY}:${appId}`;
}

export function readLocalSessions(appId) {
  if (!appId) {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(storageKey(appId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

/**
 * Record (or refresh) one session in the local index, most recent first.
 * `displayMessage` is only overwritten when a newer one is supplied, so a
 * bare touch on load does not blank out an existing preview.
 */
export function upsertLocalSession(appId, { sessionId, displayMessage, modifiedAt }) {
  if (!appId || !sessionId) {
    return readLocalSessions(appId);
  }

  const existing = readLocalSessions(appId);
  const previous = existing.find(item => item.sessionId === sessionId);
  const rest = existing.filter(item => item.sessionId !== sessionId);

  const entry = {
    sessionId,
    refId: previous?.refId || sessionId,
    displayMessage: displayMessage || previous?.displayMessage || '',
    createdAt: previous?.createdAt || modifiedAt || new Date().toISOString(),
    modifiedAt: modifiedAt || new Date().toISOString(),
    local: true,
  };

  const next = [entry, ...rest].slice(0, MAX_LOCAL_SESSIONS);
  try {
    window.localStorage.setItem(storageKey(appId), JSON.stringify(next));
  } catch (e) {
    // Storage can be full or blocked (private mode); the rail then only shows
    // whatever the API returns.
  }
  return next;
}

export function removeLocalSession(appId, sessionId) {
  const next = readLocalSessions(appId).filter(item => item.sessionId !== sessionId);
  try {
    window.localStorage.setItem(storageKey(appId), JSON.stringify(next));
  } catch (e) {
    /* ignore */
  }
  return next;
}
